
import { useEffect, useId, useState } from "react";
import { userAPI } from "../UserAPI";
import { useTranslation } from "react-i18next";
import { MeasurementTypeI18N } from "@the-real-insight/tri-model";
import i18next from "i18next";
import { parseNumeric } from "./numericFormat";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import TransmissionCounts from "../object-properties/transmissions/TransmissionCounts";

interface Properties {
  className?: string;
  device: any;
  applyConfiguration: (configuration: any) => void;
  configurationOnly?: boolean;
  hideIDField?: boolean;
  /** Needed to reach the device's own parameters; without it that tab is hidden. */
  gatewayUrl?: string;
  /** The device's asset (Mongo `_id` or business id); enables the transmission counts. */
  assetKey?: string;
}

// TODO Locale
const numberFormat0 = new Intl.NumberFormat(i18next.language, {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});
const numberFormat1 = new Intl.NumberFormat(i18next.language, {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

/** Zahlenfelder der Konfiguration; alles Übrige ist ein Textfeld. */
const NUMERIC_CONFIGURATION_TYPES = new Set(["number", "decimal", "boolean"]);

export default function DeviceProperties(properties: Properties) {
  const { t } = useTranslation();
  const fieldId = useId();
  const [catalog, setCatalog] = useState() as any;
  const [device, setDevice] = useState() as any;
  const [deviceType, setDeviceType] = useState(properties.device) as any;
  const [parameters, setParameters] = useState() as any;
  /**
   * Was in den Parameterfeldern getippt, aber noch nicht zum Gerät geschickt wurde.
   *
   * Ausdrücklich getypt statt `as any`: Es sind immer Zeichenketten — `event.target.value` ist die
   * einzige Quelle —, und erst durch den Typ weiß die Umwandlung weiter unten, dass sie eine
   * Zeichenkette in der Hand hält. Ohne ihn kam aus `Object.entries` ein `unknown`, das weder in
   * `number | string` passt noch an `parseNumeric` übergeben werden darf.
   */
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("") as any;

  useEffect(() => {
    setDevice(properties.device);
    setParameters(undefined);
    setEdits({});
    setMessage("");

    if (properties.device) {
      const call = async () => {
        try {
          const newCatalog = await userAPI.getDeviceTypeCatalog();

          console.log("New catalog >>>", newCatalog);
          console.log("Device >>>", properties.device);

          setCatalog(newCatalog);

          const manufacturer = properties.device.type.split("/")[0];
          const type = properties.device.type.split("/")[1];

          if (
            newCatalog &&
            newCatalog[manufacturer] &&
            newCatalog[manufacturer][type]
          ) {
            setDeviceType(newCatalog[manufacturer][type]);
          } else {
            console.warn("Device type not found in catalog:", {
              manufacturer: properties.device.manufacturer,
              type: properties.device.type,
              availableManufacturers: Object.keys(newCatalog || {}),
            });
            setDeviceType(null);
          }
        } catch (error) {
          console.error("Failed to load device catalog:", error);
          setCatalog(null);
          setDeviceType(null);
        }
      };

      call();
    }
  }, [properties.device]);

  /** Anzeigewert eines Zustandsfelds — bewusst als Rohwert, nicht als String. */
  const stateValue = (field: any) => {
    // `state` is where the current readings live; the flat property is the older
    // shape other device types still use. Reading both means neither has to be
    // migrated to show a value here.
    const value = device?.readings?.[field.id] ?? device?.[field.id];

    // Explicitly against undefined, not falsy: 0 bar is a reading, and printing
    // it as "-" would hide exactly the case worth seeing.
    if (value === undefined || value === null || value === "") return "-";

    switch (field.type) {
      case "decimal":
        return typeof value === "number" ? numberFormat1.format(value) : value;
      case "integer":
        return typeof value === "number" ? numberFormat0.format(value) : value;
      case "boolean":
        return typeof value === "boolean" ? String(value) : value;
      case "enum":
      case "string":
      default:
        return value;
    }
  };

  /**
   * Firmware build, as the device reports it once per boot.
   *
   * Shown beside the readings rather than under configuration because it is what
   * says how the readings should be read: the protocol carries a four-bit
   * version nobody is obliged to increment, and the alarm bitmask has already
   * changed meaning once without it moving. The build id is the only reliable
   * answer to "which semantics does this device speak".
   */
  const renderFirmware = () => {
    const firmware = device?.firmware;

    if (!firmware?.build) return null;

    const rows: Array<[string, string]> = [
      [t("settingsPanel.gatewayPanel.firmwareBuild"), firmware.build],
    ];

    if (firmware.hardwareRevision) {
      rows.push([
        t("settingsPanel.gatewayPanel.hardwareRevision"),
        firmware.hardwareRevision,
      ]);
    }

    if (firmware.reportedAt) {
      rows.push([
        t("settingsPanel.gatewayPanel.firmwareReportedAt"),
        new Date(firmware.reportedAt).toLocaleString(i18next.language),
      ]);
    }

    return (
      <div className="mb-4 max-w-md rounded-md border p-3">
        {rows.map(([label, value]) => (
          <div key={label} className="mb-1 flex flex-wrap gap-2 last:mb-0">
            <div className="flex w-48 shrink-0">
              <div className="text-sm font-bold">{label}:</div>
            </div>
            <div className="font-mono text-sm">{value}</div>
          </div>
        ))}
        {firmware.dirty ? (
          <div className="mt-2 text-sm text-amber-700">
            {t("settingsPanel.gatewayPanel.firmwareDirty")}
          </div>
        ) : null}
      </div>
    );
  };

  const renderStateContent = () => (
    <div className="mb-6 mt-5">
      {properties.assetKey ? (
        <TransmissionCounts assetKey={properties.assetKey} className="mb-5 max-w-md" />
      ) : null}
      {renderFirmware()}
      <div className="mt-5 max-w-md">
        {deviceType.state && Array.isArray(deviceType.state) ? (
          deviceType.state.map((field: any) => (
            <div key={field.id} className="mb-2 flex flex-wrap gap-2">
              <div className="flex w-48 shrink-0">
                <div className="text-sm font-bold">
                  {MeasurementTypeI18N.en[field.id]}:
                </div>
              </div>
              <div>
                <div className="text-sm">
                  {stateValue(field)}
                  {field.unit ? ` ${field.unit}` : ""}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-sm">No state information available</div>
        )}
      </div>
    </div>
  );

  const loadParameters = async () => {
    if (!properties.gatewayUrl || !device?._id) return;

    try {
      setParameters(await userAPI.getDeviceConfiguration(properties.gatewayUrl, device._id));
    } catch (error: any) {
      setMessage(error?.message ?? String(error));
    }
  };

  useEffect(() => {
    loadParameters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [properties.gatewayUrl, device?._id]);

  const runCommand = async (command: () => Promise<any>) => {
    setBusy(true);
    setMessage("");

    try {
      setParameters(await command());
      setEdits({});
    } catch (error: any) {
      setMessage(error?.message ?? String(error));
    } finally {
      setBusy(false);
    }
  };

  /**
   * The device's own parameters.
   *
   * Everything here is asynchronous with the device, and the interface has to
   * say so rather than imply otherwise: a FireIQ device is asleep between
   * uplinks, so a write is queued and only applied when it next calls in — at
   * the default reporting interval, hours away. Showing a saved value as current
   * would be the one thing this screen must not do.
   */
  const renderDeviceParameters = () => {
    const catalogue = deviceType?.configurationParameters;

    if (!Array.isArray(catalogue) || !properties.gatewayUrl) return null;

    const values = parameters?.values ?? {};
    const pendingUpdate = parameters?.pendingUpdate;
    const pendingQuery = parameters?.pendingQuery;

    return (
      <div className="mt-5">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={() =>
              runCommand(() =>
                userAPI.queryDeviceConfiguration(properties.gatewayUrl!, device._id),
              )
            }
          >
            {t("settingsPanel.gatewayPanel.parametersRead")}
          </Button>
          <Button
            size="sm"
            disabled={busy || Object.keys(edits).length === 0}
            onClick={() =>
              runCommand(() =>
                userAPI.updateDeviceConfiguration(
                  properties.gatewayUrl!,
                  device._id,
                  Object.entries(edits)
                    .map(([name, value]) => {
                      const param = catalogue.find((p: any) => p.name === name);
                      let convertedValue: number | string = value;
                      if (param?.type !== "string" && value !== "") {
                        const isNegative = String(value).trim().startsWith("-");
                        const parsed = parseNumeric(value);
                        if (parsed === null) return null;
                        convertedValue = isNegative ? -parsed : parsed;
                      }
                      return { name, value: convertedValue };
                    })
                    .filter((entry): entry is { name: string; value: number | string } => entry !== null),
                ),
              )
            }
          >
            {t("settingsPanel.gatewayPanel.parametersQueueWrite")}
          </Button>
        </div>

        {pendingUpdate || pendingQuery ? (
          <div className="mb-3 rounded-md border border-amber-300 bg-amber-50 p-2 text-sm text-amber-900">
            {t("settingsPanel.gatewayPanel.parametersPending")}
          </div>
        ) : null}

        {parameters?.valuesAt ? (
          <div className="mb-3 text-xs text-muted-foreground">
            {t("settingsPanel.gatewayPanel.parametersReadAt")}:{" "}
            {new Date(parameters.valuesAt).toLocaleString(i18next.language)}
          </div>
        ) : (
          <div className="mb-3 text-xs text-muted-foreground">
            {t("settingsPanel.gatewayPanel.parametersNeverRead")}
          </div>
        )}

        {message ? <div className="mb-3 text-sm text-red-700">{message}</div> : null}

        <div className="max-w-2xl">
          {catalogue.map((parameter: any) => (
            <div key={parameter.id} className="mb-2 flex flex-wrap items-center gap-2">
              <div className="w-56 shrink-0">
                <div className="font-mono text-xs">{parameter.name}</div>
                <div className="text-xs text-muted-foreground">
                  {parameter.type}
                  {parameter.unit ? ` · ${parameter.unit}` : ""}
                </div>
              </div>
              <Input
                className="h-8 w-48"
                value={
                  edits[parameter.name] !== undefined
                    ? edits[parameter.name]
                    : (values[parameter.name] ?? "")
                }
                placeholder={values[parameter.name] === undefined ? "—" : ""}
                onChange={(event: any) => {
                  setEdits({
                    ...edits,
                    [parameter.name]: event.target.value,
                  });
                }}
              />
              {parameter.caution ? (
                <div className="max-w-sm text-xs text-amber-700">{parameter.caution}</div>
              ) : null}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderConfigurationContent = () => (
    <div className="mt-5 flex flex-wrap gap-4">
      <div className="min-w-0 flex-1 basis-64">
        <div className="mt-5 max-w-xs">
          {deviceType.configuration && Array.isArray(deviceType.configuration) ? (
            deviceType.configuration
              .filter((field: any) =>
                properties.hideIDField ? field.id !== "id" : true
              )
              .map((field: any) => (
                <div key={field.id} className="mb-2 flex flex-col gap-1.5">
                  <Label htmlFor={`${fieldId}-${field.id}`}>{field.id}</Label>
                  {field.type === "enum" ? (
                    <Select
                      value={device[field.id]}
                      onValueChange={(value) => {
                        const newAsset = { ...device } as any;

                        newAsset[field.id] = value;

                        setDevice(newAsset);
                      }}
                    >
                      <SelectTrigger id={`${fieldId}-${field.id}`}>
                        <SelectValue placeholder="Bitte auswählen ..." />
                      </SelectTrigger>
                      <SelectContent className="max-h-72">
                        {field.values.map((x: any) => (
                          <SelectItem key={x} value={String(x)}>
                            {x}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Input
                        id={`${fieldId}-${field.id}`}
                        type={
                          NUMERIC_CONFIGURATION_TYPES.has(field.type)
                            ? "number"
                            : undefined
                        }
                        value={device[field.id] ?? ""}
                        onChange={(event) => {
                          const newAsset = { ...device } as any;

                          newAsset[field.id] = event.target.value;

                          setDevice(newAsset);
                        }}
                      />
                      {field.unit ? (
                        <span className="shrink-0 text-sm text-muted-foreground">
                          {field.unit}
                        </span>
                      ) : null}
                    </div>
                  )}
                </div>
              ))
          ) : (
            <div className="text-sm">No configuration information available</div>
          )}
        </div>
      </div>
      <div>
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            const configuration: any = {};

            if (
              deviceType.configuration &&
              Array.isArray(deviceType.configuration)
            ) {
              deviceType.configuration.forEach(
                (field: any) => (configuration[field.id] = device[field.id])
              );
            }

            properties.applyConfiguration(configuration);
          }}
        >
          {t("global.apply")}
        </Button>
      </div>
    </div>
  );

  return (
    <div>
      {catalog && deviceType ? (
        properties.configurationOnly ? (
          <div>
            <div className="text-lg font-semibold">
              {t("settingsPanel.gatewayPanel.configuration")}
            </div>
            {renderConfigurationContent()}
          </div>
        ) : (
          <Tabs defaultValue="deviceData">
            <TabsList>
              <TabsTrigger value="deviceData">
                {t("settingsPanel.gatewayPanel.deviceData")}
              </TabsTrigger>
              <TabsTrigger value="configuration">
                {t("settingsPanel.gatewayPanel.configuration")}
              </TabsTrigger>
              {deviceType?.configurationParameters && properties.gatewayUrl ? (
                <TabsTrigger value="deviceParameters">
                  {t("settingsPanel.gatewayPanel.deviceParameters")}
                </TabsTrigger>
              ) : null}
            </TabsList>
            <TabsContent value="deviceData">{renderStateContent()}</TabsContent>
            <TabsContent value="configuration">
              {renderConfigurationContent()}
            </TabsContent>
            <TabsContent value="deviceParameters">
              {renderDeviceParameters()}
            </TabsContent>
          </Tabs>
        )
      ) : (
        <div className="p-4">
          <div className="text-sm">Device information not available</div>
          {properties.device && (
            <div className="mt-3 text-sm">
              <div>
                Device ID: {properties.device._id || properties.device.id}
              </div>
              <div>
                Manufacturer: {properties.device.manufacturer || "Unknown"}
              </div>
              <div>Type: {properties.device.type || "Unknown"}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
